var abGlyphs = window.abGlyphs || ((t) => (/[\u0590-\u08ff\u0900-\u0dff\ufb1d-\ufefc]/.test(t) ? t.split(/(\s+)/).filter(Boolean) : [...t])); // shared by the MOGRT scripts, so a page loading two of them still runs both; joined scripts (Arabic, Hindi) animate by word so letters stay connected
/*
 * MOGRT use cases (sections/mogrt-uses.liquid). Each tile plays its
 * animation over and over while it's in view, the way the template would
 * on a timeline: counters count up to their value, countdowns run down,
 * text animates in and out, notifications slide down and away, paper
 * crumples open and shut. A hover (or a tap on touch screens) replays a
 * tile from the start. With reduced motion, or before this runs, every
 * tile shows its final frame. The wiggling letters run on their own, from
 * assets/mogrt-uses-wiggle.js.
 */
(() => {
  const still = window.matchMedia('(prefers-reduced-motion: reduce)');
  const pointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const wait = (ms, run) => new Promise((resolve) => setTimeout(resolve, ms)).then(() => run.alive);

  const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

  // ---- the kinds: each returns a loop (one pass, awaiting between steps) ----

  const counter = (el) => {
    const value = el.querySelector('.mu-number__value');
    const from = parseFloat(el.dataset.from) || 0;
    const to = parseFloat(el.dataset.to) || 0;
    const decimals = Number(el.dataset.decimals) || 0;
    const separator = el.dataset.separator || '';
    const format = (n) => {
      const [whole, part] = Math.abs(n).toFixed(decimals).split('.');
      const grouped = separator ? whole.replace(/\B(?=(\d{3})+(?!\d))/g, separator) : whole;
      return `${n < 0 ? '-' : ''}${grouped}${part ? `.${part}` : ''}`;
    };
    return {
      rest: () => (value.textContent = format(to)),
      async pass(run) {
        const duration = 1700;
        const start = performance.now();
        await new Promise((resolve) => {
          const step = (now) => {
            if (!run.alive) return resolve();
            const t = Math.min(1, (now - start) / duration);
            const text = format(from + (to - from) * easeInOut(t));
            if (value.textContent !== text) value.textContent = text;
            t < 1 ? requestAnimationFrame(step) : resolve();
          };
          value.textContent = format(from);
          requestAnimationFrame(step);
        });
        return wait(2400, run);
      },
    };
  };

  const countdown = (el) => {
    const value = el.querySelector('.mu-number__value');
    const parts = (el.dataset.from || '00:10').split(':').map(Number);
    while (parts.length < 3) parts.unshift(0);
    const total = (parts[0] * 3600 + parts[1] * 60 + parts[2]) * 1000;
    const withHours = parts[0] > 0 && el.dataset.hours !== 'hide';
    const hundredths = Number(el.dataset.ms) > 0;
    const two = (n) => String(n).padStart(2, '0');
    const format = (ms) => {
      const s = Math.floor(ms / 1000);
      const clock = withHours
        ? `${two(Math.floor(s / 3600))}:${two(Math.floor(s / 60) % 60)}:${two(s % 60)}`
        : `${two(Math.floor(s / 60))}:${two(s % 60)}`;
      return hundredths ? `${clock}.${two(Math.floor((ms % 1000) / 10))}` : clock;
    };
    // long timers run a short stretch, then start over
    const run_for = Math.min(total, 12000);
    return {
      rest: () => (value.textContent = format(total)),
      async pass(run) {
        const start = performance.now();
        await new Promise((resolve) => {
          const step = (now) => {
            if (!run.alive) return resolve();
            const gone = Math.min(run_for, now - start);
            const text = format(Math.max(0, total - gone));
            if (value.textContent !== text) value.textContent = text;
            gone < run_for ? requestAnimationFrame(step) : resolve();
          };
          requestAnimationFrame(step);
        });
        return wait(1600, run);
      },
    };
  };

  // Smooth Text: one start per letter, word or line (the template's
  // Animate), each piece gliding in from its offset (see the CSS); the out
  // goes by the same pieces, or by word
  const text = (el) => {
    const by = el.dataset.by || 'chars';
    const out = el.dataset.muOut || 'same';
    const step = { chars: 70, words: 170, lines: 240 }[by] || 70;
    const outStep = { chars: 20, words: 120, lines: 160 }[by] || 20;
    let index = 0;
    let word = 0;
    let last = 0;
    let lastOut = 0;
    el.querySelectorAll('.mu-text__line').forEach((line, row) => {
      const words = line.textContent.split(/(\s+)/);
      line.textContent = '';
      words.forEach((chunk) => {
        if (!chunk) return;
        if (/^\s+$/.test(chunk)) return line.append(chunk);
        const pieces = by === 'chars' ? abGlyphs(chunk) : [chunk];
        pieces.forEach((piece) => {
          const span = document.createElement('span');
          span.dataset.muPiece = '';
          span.textContent = piece;
          const order = by === 'lines' ? row : by === 'words' ? word : index;
          last = order * step;
          lastOut = out === 'words' ? word * 120 : order * outStep;
          span.style.setProperty('--d', last);
          span.style.setProperty('--o', lastOut);
          line.append(span);
          index += 1;
        });
        word += 1;
      });
    });
    // in (the last piece's start plus its glide), a still, then the way out
    return animated(el, last + 1100 + 1300, lastOut + 700);
  };

  // an element with an in and an out: armed (waiting), in, out, back to armed
  const animated = (el, hold, leave) => ({
    rest() {
      el.removeAttribute('data-armed');
      el.removeAttribute('data-out');
      el.setAttribute('data-in', '');
    },
    async pass(run) {
      el.removeAttribute('data-in');
      el.removeAttribute('data-out');
      el.setAttribute('data-armed', '');
      el.getBoundingClientRect();
      if (!(await wait(260, run))) return false;
      el.setAttribute('data-in', '');
      if (!(await wait(hold, run))) return false;
      el.setAttribute('data-out', '');
      return wait(leave, run);
    },
  });

  // a notification, timed like the template: it slides down from under a
  // line just above it, holds, slides back up, and the next one replaces it.
  // With several box colours it lands in the first, then cuts through the
  // rest (a cut every 170 ms, as in the template's demo) and stays on the last.
  const note = (el) => {
    const colors = (el.dataset.colors || '').split(',').map((c) => c.trim()).filter(Boolean);
    const paint = (color) => {
      el.style.setProperty('--mu-box', color);
      el.toggleAttribute('data-mu-light', /^(#fff|#ffffff|white)$/i.test(color));
    };
    return {
      rest() {
        el.removeAttribute('data-armed');
        el.removeAttribute('data-out');
        el.setAttribute('data-in', '');
        if (colors.length) paint(colors[colors.length - 1]);
      },
      async pass(run) {
        el.removeAttribute('data-in');
        el.removeAttribute('data-out');
        el.setAttribute('data-armed', '');
        if (colors.length) paint(colors[0]);
        el.getBoundingClientRect();
        if (!(await wait(300, run))) return false;
        el.setAttribute('data-in', '');
        let held = 0;
        if (colors.length > 1) {
          if (!(await wait(1400, run))) return false;
          for (const color of colors.slice(1)) {
            paint(color);
            if (!(await wait(170, run))) return false;
          }
          held = 1400 + (colors.length - 1) * 170;
        }
        if (!(await wait(Math.max(2000, 3800 - held), run))) return false;
        el.setAttribute('data-out', '');
        return wait(1000, run);
      },
    };
  };

  // the paper crumple reveal (assets/mogrt-paper-crumple.css): one pass from
  // the ball, paused in the hold so the clean picture stays a little longer;
  // with sound it runs straight through, like the clip under it
  const paper = (el) => {
    const loop = parseFloat(getComputedStyle(el).getPropertyValue('--mpc-loop')) * 1000 || 2800;
    const hold = el.closest('.mogrt-uses__screen--sound') ? 0 : 1700;
    const clear = () => ['data-armed', 'data-play', 'data-hold'].forEach((name) => el.removeAttribute(name));
    return {
      rest: clear,
      async pass(run) {
        clear();
        el.setAttribute('data-armed', '');
        el.getBoundingClientRect();
        if (!(await wait(450, run))) return false;
        el.setAttribute('data-play', '');
        if (hold) {
          if (!(await wait(loop * 0.45, run))) return false;
          el.setAttribute('data-hold', '');
          if (!(await wait(hold, run))) return false;
          el.removeAttribute('data-hold');
          if (!(await wait(loop * 0.55, run))) return false;
        } else if (!(await wait(loop, run))) return false;
        return wait(500, run);
      },
    };
  };

  const kinds = {
    '[data-mu-counter]': counter,
    '[data-mu-countdown]': countdown,
    '[data-mu-text]': text,
    '[data-mu-note]': note,
    '[data-mu-paper]': paper,
  };

  const init = (screen) => {
    if (screen.hasAttribute('data-mu-ready')) return;
    screen.setAttribute('data-mu-ready', '');
    const selector = Object.keys(kinds).find((s) => screen.querySelector(s));
    if (!selector) return;
    const player = kinds[selector](screen.querySelector(selector));
    let run = { alive: false };

    const stop = () => {
      run.alive = false;
      player.rest();
    };
    const start = async () => {
      run.alive = false;
      if (still.matches) return player.rest();
      const mine = (run = { alive: true });
      while (mine.alive && (await player.pass(mine)) !== false && mine.alive);
    };

    player.rest();
    let visible = false;
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        ([entry]) => {
          visible = entry.isIntersecting;
          visible ? start() : stop();
        },
        { threshold: 0.55 }
      ).observe(screen);
    }
    const tile = screen.closest('.mogrt-uses__tile') || screen;
    if (pointer.matches) tile.addEventListener('pointerenter', () => visible && start());
    else tile.addEventListener('click', () => start());
  };

  const arm = (root) => root.querySelectorAll('[data-mu-screen]').forEach(init);
  arm(document);
  document.addEventListener('shopify:section:load', (event) => arm(event.target));
})();

